import React, { useEffect, useState } from "react";
import Layout from "components/sales/layout/layout";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MrfTypeForm from "../MrfTypeForm";

const EditMrfType = ({
  getMrfTypeDropdownData,
  getMrfTypeList,
  editMrfType,
  dropdownData,
  mrfTypeData,
}) => {
  const { id } = useParams();

  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // =====================================================
  // 🔹 Fetch dropdowns + record from API
  // =====================================================
  useEffect(() => {
    setIsLoading(true);

    const loadData = async () => {
      try {
        await getMrfTypeDropdownData();
        await getMrfTypeList(null, 1, id);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load MRF Type");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id]);

  // =====================================================
  // 🔹 When mrfTypeData arrives → Normalize & set form
  // =====================================================
  useEffect(() => {
    if (!mrfTypeData || !dropdownData) return;

    const d = mrfTypeData;

    const normalized = {
      id: d.id,
      code: d.erp_code || "",
      close_mrf: d.meta_details?.close_mrf || 0,
      mrf_type: d.name || "",

      contract_no_mandatory: d.meta_details?.contract_no_mandatory ? 1 : 0,
      customer_no_mandatory: d.meta_details?.customer_no_mandatory ? 1 : 0,

      // ------- Park Capex -------
      park_capex_scope: d.meta_details?.park_capex_scope || "",
      park_capex_site: d.meta_details?.park_capex_site || "",
      park_capex_txn: d.meta_details?.park_capex_txn || "",
      park_capex_post_journal: d.meta_details?.park_capex_post_journal || 0,

      // ------- Park Opex -------
      park_opex_scope: d.meta_details?.park_opex_scope || "",
      park_opex_site: d.meta_details?.park_opex_site || "",
      park_opex_txn: d.meta_details?.park_opex_txn || "",
      park_opex_fixed_asset_creation: d.meta_details?.park_opex_fixed_asset_creation ? 1 : 0,
      park_opex_gen_prod_posting: d.meta_details?.park_opex_gen_prod_posting || "",
      park_opex_post_journal: d.meta_details?.park_opex_post_journal || 0,

      // ------- Client Capex -------
      client_capex_scope: d.meta_details?.client_capex_scope || "",
      client_capex_site: d.meta_details?.client_capex_site || "",
      client_capex_txn: d.meta_details?.client_capex_txn || "",
      client_capex_gen_prod_posting: d.meta_details?.client_capex_gen_prod_posting || "",
      client_capex_post_journal: d.meta_details?.client_capex_post_journal || 0,

      // ------- Client Opex -------
      client_opex_scope: d.meta_details?.client_opex_scope || "",
      client_opex_site: d.meta_details?.client_opex_site || "",
      client_opex_txn: d.meta_details?.client_opex_txn || "",
      client_opex_post_journal: d.meta_details?.client_opex_post_journal || 0,

      // ------- JV GL -------
      debit_gl_account: d.meta_details?.debit_gl || "",
      credit_gl_account: d.meta_details?.credit_gl || "",

      // ------- Item Journal -------
      item_journal_gen_posting: d.meta_details?.item_journal_gen_posting || "",

      // ------- Material Return -------
      material_return: d.meta_details?.material_return || 0,
      material_return_txn: d.meta_details?.material_return_txn || "",
      material_return_gen_prod_posting: d.meta_details?.material_return_gen_prod_posting || "",
      depreciation_rate: d.meta_details?.depreciation_rate || "",

      // ------- Empty Combination -------
      empty_scope: d.meta_details?.empty_scope || "",
      empty_site: d.meta_details?.empty_site || "",
      empty_txn: d.meta_details?.empty_txn || "",

      // ------- Transfer-to-Bin -------
      faulty_bin: d.meta_details?.faulty_bin || 0,
      bin_material_status: d.meta_details?.bin_material_status || "",

      // ------- Tag Return -------
      tag_return: d.meta_details?.tag_return || 0,

      // ------- Dismantle -------
      dismantle: d.meta_details?.dismantle || 0,

      // ------- Validations -------
      validation_type_of_site: d.meta_details?.validation_type_of_site || 0,
      validation_neg_sale_posted: d.meta_details?.validation_neg_sale_posted || 0,
    };

    setFormData(normalized);
  }, [mrfTypeData]);

  // =====================================================
  // 🔹 Generic Input Handler
  // =====================================================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const normalized =
      value && typeof value === "object" && value.id !== undefined ? value.id : value;
    setFormData((prev) => ({ ...prev, [name]: normalized }));
  };

  // =====================================================
  // 🔹 Submit Handler
  // =====================================================
  const handleSubmit = async () => {

      if (!formData.code || !formData.mrf_type) {
        toast.error("Code and MRF Type are mandatory");
        return;
      }

      setIsLoading(true);

      const payload = { ...formData, id, is_edit: 1 };
      const res = await editMrfType(payload);

      if (res.status == 0) {
        toast.success("MRF Type updated successfully!");
        window.history.back();
      } else {
        toast.error(res.message || "Failed to update MRF Type");
      }

      setIsLoading(false);

  };

  return (
    <Layout title={"Edit MRF Type"} isLoading={isLoading} isGoback={true}>
      {formData && (
        <MrfTypeForm
          formData={formData}
          setFormData={setFormData}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          dropdownData={dropdownData}
          isEdit={true}
        />
      )}
    </Layout>
  );
};

export default EditMrfType;
